/**
 * Database Write Log Viewer
 * 
 * Utility functions to query and view database write logs from Firestore.
 * Useful for debugging production issues remotely.
 */

import { getDefaultStore } from 'jotai'
import { dbAtom, organizationIdAtom } from '../global/AtomsProvider'
import { format } from 'date-fns'

/**
 * Query database write logs from Firestore with pagination support
 * 
 * @param {Object} filters - Filter options
 * @param {string} filters.functionName - Filter by function name (e.g., 'countEmployeeBonuses', 'validateDailyStats')
 * @param {string} filters.store - Filter by store ID
 * @param {string} filters.employeeId - Filter by employee ID
 * @param {string} filters.userId - Filter by user ID
 * @param {Date} filters.startDate - Start date for timestamp filtering
 * @param {Date} filters.endDate - End date for timestamp filtering
 * @param {number} filters.limit - Maximum number of logs to return per page (default: 50)
 * @param {Object} filters.startAfter - Document snapshot to start after (for pagination)
 * @param {boolean} filters.onlyErrors - If true, only return logs that might indicate errors
 * @returns {Promise<Object>} Object containing {logs: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean}
 */
export const queryDatabaseWriteLogs = async (filters = {}) => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const organizationId = jotaiStore.get(organizationIdAtom)

  if (!db || !organizationId) {
    console.error('Database or organization ID not available')
    return { logs: [], lastDoc: null, hasMore: false }
  }

  try {
    // db is already scoped to organizations/{orgId}, so databaseWriteLogs is a subcollection
    // Path: organizations/{orgId}/databaseWriteLogs/{logId}
    let query = db.collection('databaseWriteLogs')

    // Apply filters
    if (filters.functionName) {
      query = query.where('functionName', '==', filters.functionName)
    }
    if (filters.store) {
      query = query.where('context.store', '==', filters.store)
    }
    if (filters.employeeId) {
      query = query.where('context.employeeId', '==', filters.employeeId)
    }
    if (filters.userId) {
      query = query.where('context.userId', '==', filters.userId)
    }
    if (filters.startDate) {
      query = query.where('timestamp', '>=', filters.startDate)
    }
    if (filters.endDate) {
      query = query.where('timestamp', '<=', filters.endDate)
    }

    // Order by timestamp descending (newest first)
    query = query.orderBy('timestamp', 'desc')

    // Apply pagination - start after last document if provided
    if (filters.startAfter) {
      query = query.startAfter(filters.startAfter)
    }

    // Apply limit (default 50 for lazy loading)
    const limit = filters.limit || 50
    query = query.limit(limit + 1) // Get one extra to check if there are more

    const snapshot = await query.get()
    const logs = []
    let lastDoc = null
    let hasMore = false

    snapshot.forEach((doc, index) => {
      // If we got limit + 1 documents, the last one indicates there are more
      if (index === limit) {
        hasMore = true
        return
      }
      
      const data = doc.data()
      logs.push({
        id: doc.id,
        ...data
      })
      lastDoc = doc
    })

    // If onlyErrors filter is enabled, filter logs that might indicate issues
    if (filters.onlyErrors) {
      const filteredLogs = logs.filter(log => {
        // Logs with high operation counts might indicate issues
        if (log.summary.totalOperations > 100) return true
        // Logs with very long durations
        if (log.duration > 10000) return true // > 10 seconds
        // Logs affecting many collections (might indicate cascading issues)
        if (log.summary.collectionsAffected > 5) return true
        return false
      })
      return { logs: filteredLogs, lastDoc, hasMore: false } // Don't paginate filtered results
    }

    return { logs, lastDoc, hasMore }
  } catch (error) {
    console.error('Error querying database write logs:', error)
    return { logs: [], lastDoc: null, hasMore: false }
  }
}

/**
 * Get a formatted summary of logs for display
 * 
 * @param {Array} logs - Array of log documents
 * @returns {string} Formatted summary string
 */
export const formatLogsSummary = (logs) => {
  if (!logs || logs.length === 0) {
    return 'No logs found'
  }

  let summary = `Found ${logs.length} log entries:\n\n`
  
  logs.forEach((log, index) => {
    const timestamp = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp)
    const duration = log.duration || 0
    
    summary += `${index + 1}. ${log.functionName}\n`
    summary += `   Timestamp: ${format(timestamp, 'yyyy-MM-dd HH:mm:ss')}\n`
    summary += `   Duration: ${duration}ms\n`
    
    if (log.context.store) {
      summary += `   Store: ${log.context.store}\n`
    }
    if (log.context.employeeId) {
      summary += `   Employee: ${log.context.employeeId}\n`
    }
    if (log.context.userId) {
      summary += `   User: ${log.context.userId}\n`
    }
    
    summary += `   Operations: ${log.summary.totalOperations} (${log.summary.directWrites} direct, ${log.summary.batches} batches)\n`
    summary += `   Collections: ${log.summary.collectionsAffected}\n`
    
    // List affected collections
    const collections = Object.keys(log.summary.collections || {})
    if (collections.length > 0) {
      summary += `   Affected Collections:\n`
      collections.forEach(col => {
        const stats = log.summary.collections[col]
        summary += `     - ${col}: ${stats.total} operations, ${stats.uniqueDocuments} documents\n`
      })
    }
    
    summary += '\n'
  })
  
  return summary
}

/**
 * Get detailed log information for a specific log ID
 * 
 * @param {string} logId - Log document ID
 * @returns {Promise<Object|null>} Log document or null if not found
 */
export const getLogDetails = async (logId) => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const organizationId = jotaiStore.get(organizationIdAtom)

  if (!db || !organizationId || !logId) {
    return null
  }

  try {
    // db is already scoped to organizations/{orgId}, so this queries the org-specific collection
    // Path: organizations/{orgId}/databaseWriteLogs/{logId}
    const doc = await db.collection('databaseWriteLogs').doc(logId).get()
    
    if (!doc.exists) {
      return null
    }

    const data = doc.data()
    return {
      id: doc.id,
      ...data
    }
  } catch (error) {
    console.error('Error getting log details:', error)
    return null
  }
}

/**
 * Format a single log entry for detailed display
 * 
 * @param {Object} log - Log document
 * @returns {string} Formatted detailed log string
 */
export const formatLogDetails = (log) => {
  if (!log) {
    return 'Log not found'
  }

  const timestamp = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp)
  const endTimestamp = log.endTimestamp?.toDate ? log.endTimestamp.toDate() : new Date(log.endTimestamp)
  
  let details = `${'='.repeat(80)}\n`
  details += `Log ID: ${log.id}\n`
  details += `Function: ${log.functionName}\n`
  details += `Timestamp: ${format(timestamp, 'yyyy-MM-dd HH:mm:ss')}\n`
  details += `End Time: ${format(endTimestamp, 'yyyy-MM-dd HH:mm:ss')}\n`
  details += `Duration: ${log.duration}ms\n\n`
  
  details += `Context:\n`
  if (log.context.store) details += `  Store: ${log.context.store}\n`
  if (log.context.employeeId) details += `  Employee: ${log.context.employeeId}\n`
  if (log.context.userId) details += `  User: ${log.context.userId}\n`
  if (log.context.date) details += `  Date: ${log.context.date}\n`
  if (log.context.dateRange) details += `  Date Range: ${JSON.stringify(log.context.dateRange)}\n`
  if (log.context.screen) details += `  Screen: ${log.context.screen}\n`
  if (log.context.platform) details += `  Platform: ${log.context.platform}\n`
  if (log.context.callerFile) details += `  Caller File: ${log.context.callerFile}\n`
  if (log.context.callerFunction) details += `  Caller Function: ${log.context.callerFunction}\n`
  
  details += `\nSummary:\n`
  details += `  Total Operations: ${log.summary.totalOperations}\n`
  details += `  Direct Writes: ${log.summary.directWrites}\n`
  details += `  Batches: ${log.summary.batches}\n`
  details += `  Collections Affected: ${log.summary.collectionsAffected}\n`
  
  // Collections details
  const collections = Object.entries(log.summary.collections || {})
  if (collections.length > 0) {
    details += `\nCollections:\n`
    collections.forEach(([collection, stats]) => {
      details += `  ${collection}:\n`
      details += `    Total: ${stats.total} operations\n`
      details += `    Unique Documents: ${stats.uniqueDocuments}\n`
      details += `    Operations: ${JSON.stringify(stats.operations)}\n`
    })
  }
  
  // Batches
  if (log.batches && log.batches.length > 0) {
    details += `\nBatches (${log.batches.length}):\n`
    log.batches.forEach((batch, index) => {
      details += `  ${index + 1}. ${batch.description || batch.id}\n`
      if (batch.timestamp) {
        const batchStart = batch.timestamp?.toDate ? batch.timestamp.toDate() : new Date(batch.timestamp)
        details += `     Start: ${format(batchStart, 'HH:mm:ss.SSS')}\n`
      }
      if (batch.endTimestamp) {
        const batchEnd = batch.endTimestamp?.toDate ? batch.endTimestamp.toDate() : new Date(batch.endTimestamp)
        details += `     End: ${format(batchEnd, 'HH:mm:ss.SSS')}\n`
        const batchDuration = new Date(batch.endTimestamp) - new Date(batch.timestamp)
        details += `     Duration: ${batchDuration}ms\n`
      }
      details += `     Operations: ${batch.operations.length}\n`

      // Print each operation in the batch with fields (useful for countEmployeeBonuses and similar flows)
      if (batch.operations && batch.operations.length > 0) {
        batch.operations.forEach((op, opIndex) => {
          const opTime = op.timestamp?.toDate ? op.timestamp.toDate() : new Date(op.timestamp)
          details += `       ${opIndex + 1}. ${op.operation} ${op.collection}/${op.documentId}\n`
          details += `          Time: ${format(opTime, 'HH:mm:ss.SSS')}\n`
          if (op.options && Object.keys(op.options).length > 0) {
            details += `          Options: ${JSON.stringify(op.options)}\n`
          }
          if (op.fields && Object.keys(op.fields).length > 0) {
            details += `          Fields: ${JSON.stringify(op.fields, null, 6).split('\n').join('\n          ')}\n`
          }
        })
      }
    })
  }
  
  // Direct operations
  if (log.operations && log.operations.length > 0) {
    details += `\nDirect Operations (${log.operations.length}):\n`
    log.operations.forEach((op, index) => {
      const opTime = op.timestamp?.toDate ? op.timestamp.toDate() : new Date(op.timestamp)
      details += `  ${index + 1}. ${op.operation} ${op.collection}/${op.documentId}\n`
      details += `     Time: ${format(opTime, 'HH:mm:ss.SSS')}\n`
      if (Object.keys(op.fields).length > 0) {
        details += `     Fields: ${JSON.stringify(op.fields, null, 6).split('\n').join('\n     ')}\n`
      }
    })
  }
  
  details += `${'='.repeat(80)}\n`
  
  return details
}

/**
 * Export logs to a readable format (for sharing/debugging)
 * 
 * @param {Array} logs - Array of log documents
 * @returns {string} Formatted export string
 */
export const exportLogs = (logs) => {
  if (!logs || logs.length === 0) {
    return 'No logs to export'
  }

  let exportText = `Database Write Logs Export\n`
  exportText += `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}\n`
  exportText += `Total Logs: ${logs.length}\n`
  exportText += `${'='.repeat(80)}\n\n`

  logs.forEach((log) => {
    exportText += formatLogDetails(log)
    exportText += '\n'
  })

  return exportText
}

